import { useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { GridRecommend } from 'libs/searchspring/components/recommendations/Recommendations'
import { calculateProductCount } from 'libs/searchspring/components/collection/filters/ProductCount'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter((s) => s.type === 'collections') || []
const popularProductsProfile = globalState.siteIds?.popularProductsProfile || 'api-test'

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Suggested Collections', type: 'collections-facets', default: collectionsSuggestions }
    ],
    mainTitle: 'Search Results'
  }
}

/**
 * ViewResultsButton component - Renders the "View Results" button for search results
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {number} props.totalResults - Total number of search results
 * @returns {JSX.Element} Rendered component
 */
export const ViewResultsButton = ({ query, totalResults }) => {
  if (!query || !totalResults) return null

  return (
    <SearchPageLink query={query} className="btn btn-border btn-primary btn-s w-fit">
      View {totalResults} Results
    </SearchPageLink>
  )
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect, onResultsUpdate }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useSearchspringContext()
  const { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady } =
    useSearchResultState({ query, results, isLoading })
  const suggestedCollectionValues = results?.facets?.find((facet) => facet.field === 'product_type')?.values?.slice(0, 4) || []

  const itemClass = 'w-full'
  const productCardSettings = {
    hideQuickAdd: true,
    hideQuickAddModal: true,
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: false,
    hideWishlist: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    if (onResultsUpdate && results) {
      const totalResults = calculateProductCount(results)
      const hasResults = totalResults > 0
      onResultsUpdate({ hasResults, totalResults })
    }
  }, [results, onResultsUpdate])

  /**
   * Renders suggestion components based on current search state.
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    let config
    if (query && hasResults) {
      config = SEARCH_CONFIG.HAS_RESULTS
    } else if (query && !hasResults) {
      config = SEARCH_CONFIG.NO_RESULTS
    } else {
      config = SEARCH_CONFIG.NO_SEARCH
    }
    const suggestionQuery = query && !hasResults ? '' : query
    return config.suggestions.map((suggestion, index) => {
      return (
        <SearchSuggestions
          key={index}
          title={suggestion.title}
          type={suggestion.type}
          query={suggestionQuery}
          collectionValues={suggestedCollectionValues}
          defaultSuggestions={suggestion.default}
        />
      )
    })
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="grid auto-rows-auto max-h-[95vh] bg-secondary overflow-y-scroll overscroll-y-none no-scrollbar">
      <div className="grid shrink-0 lg:grid-cols-main lg:items-stretch">
        {/* Main Content Section */}
        <div className="col-[1/2] lg:col-[3/-1] lg:row-[1] grid w-full h-fit auto-rows-auto gap-y-1.875 overflow-x-hidden no-scrollbar">
          <div className="flex w-full flex-col">
            <div className="flex flex-wrap justify-between items-center gap-x-1.25 px-margin gap-y-0.25 lg:min-h-[64px] py-1 lg:py-0.875 border-b border-borders-default">
              <p className="paragraph-m font-serif italic">{title}</p>
              {hasResults && !redirect && (
                <ViewResultsButton query={query} totalResults={calculateProductCount(results)} />
              )}
            </div>

            <div className="w-full">
              {/* Loading Overlay */}
              <div
                className={clsx(
                  'w-full overflow-hidden',
                  isCheckingResults || isLoading ? '' : 'h-0 opacity-0 pointer-events-none'
                )}
              >
                <SkeletonGrid itemClass={itemClass} />
              </div>
              <div className={clsx('w-full', hasResults ? '' : 'hidden')}>
                {isResultsReady && <QuickSearchResults />}
              </div>
              <div
                className={clsx('w-full', !hasResults && query && !isLoading && showNoResults ? '' : 'hidden')}
                data-dl-section-id={popularProductsProfile}
              >
                {isRecommendationsReady && (
                  <GridRecommend
                    profile={popularProductsProfile}
                    limit={4}
                    itemClass={itemClass}
                    useCssGrid
                    productCardSettings={productCardSettings}
                  />
                )}
              </div>
              <div className={clsx('w-full', query ? 'hidden' : '')}>
                {popularCategories.current && (
                  <PopularCategories content={popularCategories.current} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions Section */}
        <div
          className={clsx(
            'col-[1/2] lg:col-[1/3] grid grid-cols-2 lg:flex lg:flex-col w-full h-fit lg:h-full border-borders-default lg-max:border-t lg:border-r',
            query && !isLoading ? '' : ''
          )}
        >
          {renderSuggestions()}
        </div>
      </div>

      {/* No Results Section */}
      {!hasResults && query && !isLoading && showNoResults && (
        <div className="row-[1] flex flex-col items-center justify-center w-full text-center px-margin py-1 border-b border-borders-default">
          <span className="paragraph-xs">No results for "{query}". Please try another search term.</span>
        </div>
      )}
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content }) => {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 items-stretch w-full h-fit"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ itemClass }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 w-full h-fit">
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
    </div>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'collections-facets')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, collectionValues = [] }) => {
  const isCollectionFacets = type === 'collections-facets'
  const hasCollectionValues = collectionValues.length > 0

  return (
    <div className="flex flex-col gap-y-1 relative px-margin py-1.25 lg:py-1.5 lg:border-t first:lg:border-t-0 border-borders-default">
      <p className="paragraph-xs font-base-spaced uppercase">{title}</p>
      {isCollectionFacets && hasCollectionValues ? (
        <div className="flex flex-col gap-y-1 w-full">
          {collectionValues.map((item) => (
            <SearchPageLink
              key={`collection-${item.value}`}
              url={`/collections/${encodeURI(item.value.toLowerCase().replace(/\s+/g, '-'))}`}
              className="group flex w-fit link-fade"
            >
              <span className="paragraph-xs capitalize">{item.label}</span>
            </SearchPageLink>
          ))}
        </div>
      ) : isCollectionFacets ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
      ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
      )}
    </div>
  )
}
